<template>
  <div class="necessity">
    <HeroBanner
        :background-image="bannerImage"
        title="보청기 필요성"
        sub-title="건강한 청력을 유지·보완하기 위한 골든타임, <br>
        바로 지금입니다."
    />

    <div class="necessity-content">
      <!-- 섹션 1: 난청은 누구에게나 찾아올 수 있습니다 -->
      <div class="intro-section">
        <h2 class="section-title">난청은 누구에게나 찾아올 수 있습니다.</h2>
        <div class="section-text">
          <p>• 노화(노인성 난청): 50~60대부터 청력이 서서히 떨어짐</p>
          <p>• 소음 노출: 이어폰, 기계음등 일상 속 원인일 수 있음</p>
          <p>• 귀 질환: 중이염, 이명, 돌발성 난청 등으로 발생</p>
          <p>• 유전·건강 문제: 가족력, 당뇨·고혈압 등 만성질환 영향 가능</p>
        </div>
      </div>

      <!-- 섹션 2: 난청을 방치하지 마세요 -->
      <div class="intro-section">
        <h2 class="section-title">난청을 방치하지 마세요.</h2>
        <div class="section-text">
          <p>• 대화 불편: 가족, 친구와 소통이 어려질 수 있음</p>
          <p>• 안전 위험: 초인종, 차량 경적 등 생활 소리 놓칠 수 있음</p>
          <p>• 인지 저하 위험: 난청이 치매 발병 위험을 높일 수 있음</p>
          <p>• 심리적 문제: 우울감, 사회적 고립감 심화될 수 있음</p>
        </div>
      </div>

      <!-- 청력 손실 정도 이미지 -->
      <div class="content-image-section">
        <img
            :src="hearingLossImage"
            :srcset="`${hearingLossImage} 1x, ${hearingLossImageRetina} 2x`"
            alt="청력 손실 정도에 따라 보청기가 필요합니다"
            class="content-image"
        />
      </div>

      <!-- 청력 손실 정도 섹션 -->
      <div class="area01">
        <h2 class="hearing-title">청력 손실 정도에 따라<br>보청기가 필요합니다.</h2>

        <!-- 01 경도 난청 -->
        <div class="loss-level">
          <h3 class="level-title">경도 난청(20~40 dB)</h3>
          <div class="loss-table">
            <div class="table-row">
              <div class="table-label">주요 증상</div>
              <div class="table-value">대화할 때 작은 말소리에 어느 정도 어려움을 느끼지만 조용한 곳에서의 대화는 문제없음</div>
            </div>
            <div class="table-row">
              <div class="table-label">필요 여부</div>
              <div class="table-value">부분적 필요</div>
            </div>
          </div>
        </div>

        <!-- 02 중도/중고도 난청 -->
        <div class="loss-level">
          <h3 class="level-title">중도/ 중고도 난청(40~70dB)</h3>
          <div class="loss-table">
            <div class="table-row">
              <div class="table-label">주요 증상</div>
              <div class="table-value">가까운 거리에서도 큰 소리로 말해야 대화가 가능하고, TV 또는 라디오 볼륨을 크게 하여 들음</div>
            </div>
            <div class="table-row">
              <div class="table-label">필요 여부</div>
              <div class="table-value">적극(거의 향상) 필요</div>
            </div>
          </div>
        </div>

        <!-- 03 고도 난청 -->
        <div class="loss-level">
          <h3 class="level-title">고도 난청(70~90dB)</h3>
          <div class="loss-table">
            <div class="table-row">
              <div class="table-label">주요 증상</div>
              <div class="table-value">정상적인 대화가 어려우며 귀 가까이에서 큰 소리로 이야기해야 들림</div>
            </div>
            <div class="table-row">
              <div class="table-label">필요 여부</div>
              <div class="table-value">항상 필요</div>
            </div>
          </div>
        </div>

        <!-- 04 심도 난청 -->
        <div class="loss-level">
          <h3 class="level-title">심도 난청(90dB 이상)</h3>
          <div class="loss-table">
            <div class="table-row">
              <div class="table-label">주요 증상</div>
              <div class="table-value">정상적인 대화가 불가능하고, 보청기로 도움 받을 수 있는 부분이 제한적</div>
            </div>
            <div class="table-row">
              <div class="table-label">필요 여부</div>
              <div class="table-value">항상 필요(제한적)</div>
            </div>
          </div>
        </div>
      </div>


      <!-- 보청기 양이 착용 이미지 -->
      <div class="content-image-section p-20">
        <img
          :src="binauralBenefitImage"
          alt="보청기는 양이 착용해야 효과가 좋습니다"
          class="content-image"
        />
      </div>

      <!-- 스퀠치효과 설명 이미지 -->
      <div class="content-image-section p-20">
        <img
          :src="squelchEffectImage"
          alt="스퀠치효과 설명"
          class="content-image"
        />
      </div>
    </div>
  </div>
</template>

<script>
import HeroBanner from "@/components/HeroBanner.vue";
import bannerBg from '@/assets/images/heros/02-top-img.png';
import bannerBg2 from '@/assets/images/heros/02-top-img@2x.png';
import personImage from '@/assets/images/banners/02/02-01-img-01.png';
import hearingLossImage from '@/assets/images/banners/02/02-01-img-01.png';
import hearingLossImageRetina from '@/assets/images/banners/02/02-01-img-01@2x.png';
import binauralBenefitImage from '@/assets/images/banners/02/02-01-img-02.png';
import squelchEffectImage from '@/assets/images/banners/02/02-01-img-03.png';

export default {
  name: 'HearingAidNecessity',
  components: {HeroBanner},
  data() {
    return {
      bannerImage: [bannerBg, bannerBg2],
      personImage: personImage,
      hearingLossImage: hearingLossImage,
      hearingLossImageRetina: hearingLossImageRetina,
      binauralBenefitImage: binauralBenefitImage,
      squelchEffectImage: squelchEffectImage,
    };
  }
};
</script>

<style scoped lang="scss">
.p-20{
  padding: 0 20px;
}
.necessity {
  .necessity-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 55px 0 0;
    gap: 50px;
    width: 100%;
    max-width: 500px;
    margin: 0 auto;
    background: #FFFFFF;

    .intro-section {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      //padding: 0 36px;
      gap: 18px;
      width: 425px;
      max-width: 100%;

      .section-title {
        width: 100%;
        font-family: 'Noto Sans KR', sans-serif;
        font-style: normal;
        font-weight: 700;
        font-size: 26px;
        line-height: 40px;
        letter-spacing: -1.11607px;
        color: #222222;
        margin: 0;
      }

      .section-text {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        width: 100%;

        p {
          font-family: 'Noto Sans KR', sans-serif;
          font-style: normal;
          font-weight: 350;
          font-size: 18px;
          line-height: 30px;
          letter-spacing: -0.02em;
          color: #222222;
          margin: 0;
        }
      }
    }

    .gradient-section {
      position: relative;
      width: 100%;
      max-width: 500px;
      height: 345px;
      background: linear-gradient(180deg, #A6CDDB 0%, #E8F0F6 100%);

      .davich-logo {
        position: absolute;
        width: 115.92px;
        height: 161.67px;
        left: calc(50% - 115.92px/2 - 111.66px);
        top: 84.55px;
      }

      .person-image {
        position: absolute;
        width: 259.26px;
        height: 293.52px;
        right: 0;
        top: 51.85px;
      }
    }

    .area01 {
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: 0;
      width: 100%;
      max-width: 500px;

      .hearing-title {
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 0;
        gap: 18.52px;
        width: 100%;
        max-width: 500px;
        height: 96px;
        font-family: 'Noto Sans KR', sans-serif;
        font-style: normal;
        font-weight: 700;
        font-size: 30px;
        line-height: 48px;
        text-align: center;
        letter-spacing: -1.00446px;
        color: #222222;
        margin: 0;
      }

      .loss-level {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        padding: 36px 0;
        gap: 32px;
        width: 425px;
        max-width: 100%;
        box-sizing: border-box;

        .level-title {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          width: 100%;
          height: 40px;
          font-family: 'Noto Sans KR', sans-serif;
          font-style: normal;
          font-weight: 700;
          font-size: 24px;
          line-height: 40px;
          letter-spacing: -0.05em;
          color: #222222;
          margin: 0;
        }

        .loss-table {
          box-sizing: border-box;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: flex-start;
          width: 100%;
          border-top: 1px solid #222;
          border-bottom: 1px solid #222;
          .table-row {
            &:nth-of-type(2){
              border-top: 1px solid #DEDEDE;
            }
            display: flex;
            flex-direction: row;
            width: 100%;
            &:last-child {
              padding-bottom: 0;
            }

            .table-label {
              display: flex;
              flex-direction: row;
              justify-content: center;
              align-items: center;
              width: 111.61px;
              min-width: 111.61px;
              background: #F9F9F9;
              font-family: 'Noto Sans KR', sans-serif;
              font-style: normal;
              font-weight: 600;
              font-size: 18px;
              line-height: 30px;
              text-align: center;
              letter-spacing: -0.04em;
              color: #222222;
              padding-top: 22px;
              padding-bottom: 22px;
            }

            .table-value {
              display: flex;
              flex-direction: column;
              align-items: flex-start;
              padding: 22px;
              flex: 1;
              font-family: 'Noto Sans KR', sans-serif;
              font-style: normal;
              font-weight: 350;
              font-size: 18px;
              line-height: 30px;
              letter-spacing: -0.02em;
              color: #222222;
            }
          }
        }
      }
    }

    .content-image-section {
      width: 100%;
      //max-width: 425px;
      display: flex;
      justify-content: center;
      align-items: center;
      .content-image {
        width: 100%;
        height: auto;
        display: block;
      }
      //&:not(:nth-of-type(1)){
      //  padding: 0 36px;
      //}
    }

  }

  @media (max-width: 768px) {
    .necessity-content {
      padding: 40px 0 0;
      gap: 24px;

      .intro-section {
        padding: 0 16px;
        gap: 16px;
        width: 100%;

        .section-title {
          font-size: 24px;
          line-height: 36px;
        }

        .section-text p {
          font-size: 16px;
          line-height: 26px;
        }
      }

      .gradient-section {
        height: 300px;

        .davich-logo {
          width: 95px;
          height: 133px;
          left: calc(50% - 95px/2 - 92px);
          top: 70px;
        }

        .person-image {
          width: 215px;
          height: 243px;
          top: 43px;
        }
      }

      .area01 {
        .hearing-title {
          font-size: 26px;
          line-height: 42px;
          height: 84px;
        }

        .loss-level {
          padding: 30px 16px 0;
          gap: 28px;
          width: 100%;

          .level-title {
            font-size: 22px;
            line-height: 36px;
          }

          .loss-table .table-row {
            .table-label {
              width: 90px;
              min-width: 90px;
              font-size: 16px;
              line-height: 26px;
              padding-top: 18px;
              padding-bottom: 18px;
            }

            .table-value {
              padding: 18px;
              font-size: 16px;
              line-height: 26px;
            }
          }
        }
      }
    }
  }

  @media (max-width: 450px) {
    .necessity-content {
      padding: 30px 0 0;
      gap: 32px;

      .intro-section {
        padding: 0 20px;
        gap: 14px;

        .section-title {
          font-size: 22px;
          line-height: 32px;
        }

        .section-text p {
          font-size: 15px;
          line-height: 24px;
        }
      }

      .gradient-section {
        height: 260px;

        .davich-logo {
          width: 80px;
          height: 112px;
          left: calc(50% - 80px/2 - 77px);
          top: 60px;
        }

        .person-image {
          width: 180px;
          height: 204px;
          top: 37px;
        }
      }

      .area01 {
        .hearing-title {
          font-size: 24px;
          line-height: 38px;
          height: 76px;
        }

        .loss-level {
          padding: 24px 20px 0;
          gap: 24px;

          .level-title {
            font-size: 20px;
            line-height: 32px;
          }

          .loss-table .table-row {
            .table-label {
              width: 80px;
              min-width: 80px;
              font-size: 15px;
              line-height: 24px;
              padding-top: 16px;
              padding-bottom: 16px;
            }

            .table-value {
              padding: 16px;
              font-size: 15px;
              line-height: 24px;
            }
          }
        }
      }
    }
  }
}
</style>
